import { useInstagramMessages, useRestApiProvider } from "@jugl-web/rest-api";
import { InstagramMessageModel } from "@jugl-web/rest-api/integrations/models/InstagramMessage/InstagramMessageModel";
import {
  isoToLocalDate,
  useDebounce,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { VList, VListHandle } from "virtua";
import {
  DataLoadingWrapper,
  LoadingAnimation,
  PageLoaderFull,
} from "@jugl-web/ui-components";
import isToday from "date-fns/isToday";
import isThisYear from "date-fns/isThisYear";
import format from "date-fns/format";
import { ChatDateHeader } from "@web-src/modules/chats/pages/ChatsPage/components/ChatWindow/components/ChatMessagesWindow/components/ChatDateHeader";
import { ChatMessageReplyInfo } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageReplyInfo";
import { useDispatch } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { IntegrationsApiTag } from "@jugl-web/rest-api/integrations/tags";
import { ReactComponent as ChevronsDownIcon } from "./assets/chevrons-down.svg";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { InstagramMessageItem } from "../InstagramMessageItem";
import { isInstagramMessageDeleted } from "../../../../utils/isInstagramMessageDeleted";

export interface InstagramChatMessageWindowHandle {
  scrollToBottom: () => void;
  addMessage: (
    message: InstagramMessageModel,
    forceScrollToBottom?: boolean
  ) => void;
}

type InstagramChatMessageItem =
  | { type: "message"; value: InstagramMessageModel }
  | { type: "date"; value: string };

export const InstagramChatMessageWindow = forwardRef<
  InstagramChatMessageWindowHandle,
  { conversationId: string }
>(({ conversationId }, ref) => {
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const { toast } = useToast();
  const { dateLocale } = useLanguage();
  const vListRef = useRef<VListHandle>(null);
  const [atBottom, setAtBottom] = useState(true);
  const debouncedAtBottom = useDebounce(atBottom, 500);
  const [replyTargetId, setReplyTargetId] = useState<string>();
  const { incomingInstagramMessages$ } = useClientSupportPageContext();
  const prevLenRef = useRef(0);
  const prevScrollOffsetRef = useRef(0);
  const wasLoadingMore = useRef(false);
  const { integrationsApi } = useRestApiProvider();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<ThunkDispatch<any, unknown, AnyAction>>();

  const [getInstagramMessage] =
    integrationsApi.useLazyGetInstagramMessageByIdQuery();

  const [isInitialScrollInitialized, setIsInitialScrollInitialized] =
    useState(false);
  const isInitialScrollInitializationInProgress = useRef(false);

  const {
    data: loadedMessages,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    reset,
  } = useInstagramMessages({
    entityId,
    conversationId,
    pageSize: 50,
  });

  const instagramMessageItems = useMemo(() => {
    if (!loadedMessages.length) return [];

    const result: InstagramChatMessageItem[] = [];
    const addedDates: string[] = [];

    loadedMessages.forEach((message) => {
      const date = isoToLocalDate(message.inserted_at);

      const dateStrAlt = format(date, "yyyy-MM-dd");

      if (!addedDates.includes(dateStrAlt)) {
        addedDates.push(dateStrAlt);
        const dateStr = isToday(date)
          ? t({
              id: "common.today",
              defaultMessage: "Today",
            })
          : format(date, isThisYear(date) ? "d MMMM" : "d MMMM yyyy", {
              locale: dateLocale,
            });

        result.push({ type: "date", value: dateStr });
      }

      result.push({ type: "message", value: message });
    });

    return result;
  }, [loadedMessages, t, dateLocale]);

  // Reset when conversation changes
  useEffect(() => {
    setIsInitialScrollInitialized(false);
    reset();
  }, [conversationId, reset]);

  // Initial scroll to bottom
  useEffect(() => {
    if (
      !isInitialScrollInitialized &&
      !isInitialScrollInitializationInProgress.current &&
      instagramMessageItems.length > 0 &&
      !isLoading
    ) {
      isInitialScrollInitializationInProgress.current = true;
      // Using timeout to wait for virtua list to be ready
      setTimeout(() => {
        vListRef.current?.scrollToIndex(instagramMessageItems.length - 1, {
          align: "end",
        });
        setIsInitialScrollInitialized(true);
        isInitialScrollInitializationInProgress.current = false;
      }, 100);
    }
  }, [isLoading, isInitialScrollInitialized, instagramMessageItems.length]);

  const scrollToBottom = useCallback(() => {
    vListRef.current?.scrollToIndex(instagramMessageItems.length, {
      align: "end",
    });
    setAtBottom(true);
  }, [instagramMessageItems.length]);
  // Subscribe to incoming messages

  useEffect(() => {
    const subscription = incomingInstagramMessages$.subscribe((message) => {
      dispatch(
        integrationsApi.util.invalidateTags([
          {
            type: IntegrationsApiTag.instagramMessage,
            id: message.msg_id,
          },
        ])
      );

      if (message.conv_id !== conversationId) return;

      if (atBottom) {
        setTimeout(scrollToBottom, 100);
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingInstagramMessages$,
    conversationId,
    atBottom,
    instagramMessageItems.length,
    scrollToBottom,
    dispatch,
    integrationsApi.util,
  ]);

  const addMessage = useCallback(
    (message: InstagramMessageModel, forceScrollToBottom?: boolean) => {
      if (forceScrollToBottom || atBottom) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    },
    [atBottom, scrollToBottom]
  );

  useImperativeHandle(ref, () => ({
    scrollToBottom,
    addMessage,
  }));

  const handleLoadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore) {
      prevLenRef.current = instagramMessageItems.length;
      prevScrollOffsetRef.current = vListRef.current?.scrollOffset ?? 0;
      wasLoadingMore.current = true;
      await loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, instagramMessageItems.length]);

  useEffect(() => {
    if (isLoadingMore) {
      wasLoadingMore.current = true;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (
      wasLoadingMore.current &&
      !isLoadingMore &&
      instagramMessageItems.length > prevLenRef.current
    ) {
      vListRef.current?.scrollToIndex(
        instagramMessageItems.length - prevLenRef.current,
        {
          align: "start",
          offset: prevScrollOffsetRef.current,
        }
      );
      wasLoadingMore.current = false;
    }
    prevLenRef.current = instagramMessageItems.length;
  }, [instagramMessageItems, isLoadingMore]);

  useEffect(() => {
    if (!replyTargetId || isLoadingMore) return;

    const index = instagramMessageItems.findIndex(
      (m) => m.type === "message" && m.value.msg_id === replyTargetId
    );

    if (index >= 0) {
      setTimeout(
        () =>
          vListRef.current?.scrollToIndex(index, {
            align: "center",
            smooth: true,
          }),
        150
      );
      setReplyTargetId(undefined);
    } else if (hasMore) {
      loadMore();
    } else {
      toast(
        t({
          id: "chats-page.message-not-found",
          defaultMessage: "Message not found",
        }),
        { variant: "error" }
      );
      setReplyTargetId(undefined);
    }
  }, [
    loadMore,
    toast,
    replyTargetId,
    t,
    instagramMessageItems,
    hasMore,
    isLoadingMore,
  ]);

  const handleReplyPreviewClick = useCallback(
    async (replyInfo: ChatMessageReplyInfo) => {
      const response = await getInstagramMessage({
        entityId,
        params: { msg_id: replyInfo.replyMsgId },
      });

      const replyMessage = response.data?.data[0];

      if ("error" in response || !replyMessage) {
        toast(
          t({
            id: "client-support-page.message-not-found",
            defaultMessage: "Message not found",
          }),
          { variant: "error" }
        );
        return;
      }

      if (isInstagramMessageDeleted(replyMessage)) {
        toast(
          t({
            id: "client-support-page.source-message-was-deleted",
            defaultMessage: "Source message was deleted",
          }),
          { variant: "error" }
        );
      }

      setReplyTargetId(replyMessage.msg_id);
    },
    [entityId, getInstagramMessage, t, toast]
  );

  return (
    <>
      <PageLoaderFull isTransparent isActive={!!replyTargetId} />
      <div className="relative flex h-full flex-col bg-gray-100">
        <DataLoadingWrapper
          isError={isError}
          isFetching={false}
          isLoading={isLoading && loadedMessages.length === 0}
          onRetry={reset}
        >
          <VList
            ref={vListRef}
            reverse
            className="flex-1 pb-6"
            onScroll={(offset) => {
              if (
                !vListRef.current ||
                isLoadingMore ||
                !isInitialScrollInitialized
              ) {
                return;
              }
              const bottomOffset =
                offset -
                vListRef.current.scrollSize +
                vListRef.current.viewportSize;

              setAtBottom(bottomOffset > -5);
              // Load more messages when scrolling up
              if (offset < 150 && hasMore) {
                handleLoadMore();
              }
            }}
          >
            {isLoading && loadedMessages.length > 0 && hasMore && (
              <div className="flex justify-center py-4">
                <LoadingAnimation size="sm" />
              </div>
            )}
            {instagramMessageItems.map((item, index) => {
              if (item.type === "message") {
                return (
                  <InstagramMessageItem
                    key={item.value.id}
                    message={item.value}
                    messages={loadedMessages}
                    onReplyPreviewClick={handleReplyPreviewClick}
                  />
                );
              }
              return (
                <ChatDateHeader key={`date-${+index}`} date={item.value} />
              );
            })}
          </VList>
        </DataLoadingWrapper>

        {!debouncedAtBottom && (
          <div
            className="absolute bottom-[20px] right-[30px] z-50 flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-[18px] bg-black"
            onClick={() => scrollToBottom()}
          >
            <ChevronsDownIcon />
          </div>
        )}
      </div>
    </>
  );
});
